/**
 * Pure math for the Surface Box tool — converts a footprint rectangle on a
 * locked face plane plus a signed normal-axis height into the position,
 * Euler rotation, and non-uniform scale for a brand-new unit cube
 * (`generatePrimitive("cube")`, which spans `[-0.5, 0.5]^3`).
 *
 * No THREE dependency — keeps the math unit-testable and side-effect free.
 * The handler is the only caller; it converts plain triplets into THREE
 * objects at the boundary.
 */
import { eulerXYZFromBasis, type FaceBasis, type Vec3 } from "./faceBasis.ts";

export interface SurfaceBoxDraft {
  /** Face frame locked at footprint pointerdown. */
  basis: FaceBasis;
  /** Optional object resolved under the silhouette; not used by the math. */
  hostObjectId: string | null;
  /** First rectangle corner in world space, lying on the basis plane. */
  cornerA: Vec3;
  /** Second rectangle corner in world space, lying on the basis plane. */
  cornerB: Vec3;
  /** Signed extrusion distance along `basis.normal` (positive = outward). */
  height: number;
}

export interface CubeTransform {
  /** Object position (cube center in world space). */
  position: Vec3;
  /** XYZ Euler angles in radians, intrinsic XYZ order (matches `SceneObject.rotation`). */
  rotation: Vec3;
  /** Non-uniform scale: [|width|, |depth|, |height|]. */
  scale: Vec3;
  /** Signed footprint width along `basis.tangent`. */
  width: number;
  /** Signed footprint depth along `basis.bitangent`. */
  depth: number;
}

export interface SurfaceFootprintCornerOptions {
  /**
   * When true, `anchor` is the footprint center and `target` defines the
   * positive half-extents. When false, `anchor` is the first rectangle corner.
   */
  centered?: boolean;
  /** Constrain the footprint to a square in the locked face basis. */
  square?: boolean;
}

/**
 * Project a world-space corner onto the (u, v) plane defined by `basis`.
 * `u` runs along `basis.tangent`; `v` runs along `basis.bitangent`.
 */
export function basisUV(
  basis: FaceBasis,
  worldPoint: Vec3,
): { u: number; v: number } {
  const dx = worldPoint[0] - basis.origin[0];
  const dy = worldPoint[1] - basis.origin[1];
  const dz = worldPoint[2] - basis.origin[2];
  return {
    u: dx * basis.tangent[0] + dy * basis.tangent[1] + dz * basis.tangent[2],
    v:
      dx * basis.bitangent[0] +
      dy * basis.bitangent[1] +
      dz * basis.bitangent[2],
  };
}

/** Reconstruct a world-space point from basis (u, v). */
export function uvToWorld(basis: FaceBasis, u: number, v: number): Vec3 {
  return [
    basis.origin[0] + u * basis.tangent[0] + v * basis.bitangent[0],
    basis.origin[1] + u * basis.tangent[1] + v * basis.bitangent[1],
    basis.origin[2] + u * basis.tangent[2] + v * basis.bitangent[2],
  ];
}

/**
 * Project a world-space point onto the basis axes without subtracting
 * `basis.origin`. This is the world-anchored coordinate system used by the
 * surface alignment grid, so grid lines do not shift when a different point
 * on the same surface is used as the locked face origin.
 */
export function basisGridUV(
  basis: FaceBasis,
  worldPoint: Vec3,
): { u: number; v: number } {
  return {
    u:
      worldPoint[0] * basis.tangent[0] +
      worldPoint[1] * basis.tangent[1] +
      worldPoint[2] * basis.tangent[2],
    v:
      worldPoint[0] * basis.bitangent[0] +
      worldPoint[1] * basis.bitangent[1] +
      worldPoint[2] * basis.bitangent[2],
  };
}

/** Reconstruct a world-space point from world-anchored basis-grid (u, v). */
export function gridUvToWorld(basis: FaceBasis, u: number, v: number): Vec3 {
  const originUv = basisGridUV(basis, basis.origin);
  return uvToWorld(basis, u - originUv.u, v - originUv.v);
}

/**
 * Square clamp (Shift constraint).
 *
 * Returns the corner-B (u, v) so the rectangle is a square anchored at A,
 * with side `max(|du|, |dv|)`. `sign(0)` falls back to +1 so a fresh drag
 * with no motion still yields a positive direction (avoids collapsing to a
 * line at the very start of the drag).
 */
export function clampSquareInBasis(
  uA: number,
  vA: number,
  uB: number,
  vB: number,
): { uB: number; vB: number } {
  const du = uB - uA;
  const dv = vB - vA;
  const side = Math.max(Math.abs(du), Math.abs(dv));
  const sgnU = du < 0 ? -1 : 1;
  const sgnV = dv < 0 ? -1 : 1;
  return { uB: uA + sgnU * side, vB: vA + sgnV * side };
}

/**
 * Resolve a pointer target into actual footprint corners.
 *
 * Surface Box and Surface Cylinder share the same rectangle-footprint draft.
 * In default mode the first click is corner A; with Alt/Option held it is the
 * footprint center, matching DCC convention for center-out primitive creation.
 */
export function resolveSurfaceFootprintCorners(
  basis: FaceBasis,
  anchor: Vec3,
  target: Vec3,
  options: SurfaceFootprintCornerOptions = {},
): { cornerA: Vec3; cornerB: Vec3 } {
  const a = basisUV(basis, anchor);
  let b = basisUV(basis, target);

  if (options.square) {
    const clamped = clampSquareInBasis(a.u, a.v, b.u, b.v);
    b = { u: clamped.uB, v: clamped.vB };
  }

  if (!options.centered) {
    return {
      cornerA: [anchor[0], anchor[1], anchor[2]],
      cornerB: uvToWorld(basis, b.u, b.v),
    };
  }

  const du = b.u - a.u;
  const dv = b.v - a.v;
  return {
    cornerA: uvToWorld(basis, a.u - du, a.v - dv),
    cornerB: uvToWorld(basis, a.u + du, a.v + dv),
  };
}

/**
 * Footprint area, in the basis plane.
 *
 * Used by the handler to decide whether a footprint pointerup confirms or
 * cancels (a sub-epsilon area is a misclick).
 */
export function footprintAreaInBasis(draft: SurfaceBoxDraft): {
  width: number;
  depth: number;
  area: number;
} {
  const a = basisUV(draft.basis, draft.cornerA);
  const b = basisUV(draft.basis, draft.cornerB);
  const width = b.u - a.u;
  const depth = b.v - a.v;
  return { width, depth, area: Math.abs(width) * Math.abs(depth) };
}

/**
 * Build the world-space cube transform from the draft.
 *
 * The cube is the unit primitive `[-0.5, 0.5]^3`, so the non-uniform scale
 * (|width|, |depth|, |height|) resizes it to span the footprint × height
 * box. The cube is centered on the footprint and offset by `height/2`
 * along the basis normal so it straddles the host face by half its depth
 * — confirming negative heights extrude into the host without flipping
 * vertex winding.
 *
 * Sign of the footprint axes is folded into the centered position; signs
 * are dropped from `scale` (Three.js prefers non-negative scale to keep
 * normals oriented consistently).
 */
export function cubeTransformFromDraft(draft: SurfaceBoxDraft): CubeTransform {
  const { basis, height } = draft;
  const a = basisUV(basis, draft.cornerA);
  const b = basisUV(basis, draft.cornerB);
  const width = b.u - a.u;
  const depth = b.v - a.v;
  const cu = (a.u + b.u) / 2;
  const cv = (a.v + b.v) / 2;

  const center: Vec3 = [
    basis.origin[0] +
      cu * basis.tangent[0] +
      cv * basis.bitangent[0] +
      (height / 2) * basis.normal[0],
    basis.origin[1] +
      cu * basis.tangent[1] +
      cv * basis.bitangent[1] +
      (height / 2) * basis.normal[1],
    basis.origin[2] +
      cu * basis.tangent[2] +
      cv * basis.bitangent[2] +
      (height / 2) * basis.normal[2],
  ];

  const rotation = eulerXYZFromBasis(basis);

  return {
    position: center,
    rotation,
    scale: [Math.abs(width), Math.abs(depth), Math.abs(height)],
    width,
    depth,
  };
}
